import React, { useCallback, useState } from "react";
import { MDBContainer } from "mdbreact";
import addNotification from "helpers/notify";
import Loading from "components/ui-components/loading";
import { AcquireLicenseModal } from "components/AcquireLicense/AcquireLicenseModal";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { SCOPE_BIDLIGHT, otherVersionFilters } from "helpers/estimatingDatabase/databaseRows";
import { DatabaseHeader } from "./DatabaseHeader";
import { DatabaseFiltrationBar } from "./DatabaseFiltrationBar";
import { SaveToVersionModal, NewVersionModal, confirmDialog } from "./DatabaseModals";

// Header + filtration bar + save plumbing shared by every estimating
// database page. `lists` are useScopedList results, saved in order.
export const DatabasePageShell = ({
  title,
  workspace,
  lists,
  searchValue,
  setSearchValue,
  onAdd,
  onEdit,
  onDelete,
  onClone,
  onExport,
  onImport,
  addLabel,
  disableAdd,
  beforeSave,
  children,
}) => {
  const [busy, setBusy] = useState(false);
  const [versionModal, setVersionModal] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [licenseType, setLicenseType] = useState(null);

  const pending = lists.reduce((s, l) => s + (l.pending || 0), 0);
  const loading = busy || workspace.templatesLoading || lists.some((l) => l.loading);
  const bidlight = workspace.scope === SCOPE_BIDLIGHT;

  const guard = useCallback(
    (permission, fn) => (...args) => {
      const needed =
        permission === "delete" ? ALL_PERMISSIONS.PRICING_DATABASE_DELETE : ALL_PERMISSIONS.PRICING_DATABASE_EDIT;
      const ok = permission === "delete" ? workspace.canDelete : workspace.canEdit;
      if (!ok) {
        setLicenseType(needed);
        return undefined;
      }
      return fn(...args);
    },
    [workspace.canDelete, workspace.canEdit]
  );

  // The desktop asks before a reload throws away unsaved edits.
  const confirmLeave = useCallback(
    (fn) => {
      if (!pending || bidlight) {
        fn();
        return;
      }
      confirmDialog({
        title: "Discard unsaved changes?",
        content: `There ${pending > 1 ? "are" : "is"} ${pending} unsaved change${pending > 1 ? "s" : ""} on this version.`,
        okText: "Discard",
        onOk: fn,
      });
    },
    [pending, bidlight]
  );

  const report = (errors, okMessage) => {
    if (errors.length) {
      addNotification([...new Set(errors)].slice(0, 3).join(" / "), "danger");
    } else {
      addNotification(okMessage, "success");
    }
  };

  // DatabaseTabViewModel: both copies need ticked changes
  // (GetDataChangedCount > 0) and then send only the ticked rows.
  const copyChanges = lists.reduce((s, l) => s + (l.copyChanges || 0), 0);

  // ExecuteLoadItems for the chosen version: the country / currency check
  // and, for a BidLight version, the company copy first.
  const loadVersion = async (t) => {
    if (!workspace.loadVersion) return { created: false };
    setBusy(true);
    try {
      const r = await workspace.loadVersion(t);
      if (r.error) addNotification(r.error, "danger");
      return r;
    } finally {
      setBusy(false);
    }
  };
  const onPickVersion = (t) => loadVersion(t);
  const onLoad = async () => {
    const r = await loadVersion(workspace.template);
    if (!r.error && !r.created) await reloadAll();
  };

  const reloadAll = async () => {
    for (const l of lists) {
      // eslint-disable-next-line no-await-in-loop
      await l.reload();
    }
  };

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const results = [];
      for (const l of lists) {
        // eslint-disable-next-line no-await-in-loop
        results.push(await fn(l));
      }
      const errors = results.flatMap((r) => r.errors || []);
      const count = results.reduce((s, r) => s + (r.count || 0), 0);
      if (!count && !errors.length) {
        addNotification("There is no items selected for saving.", "danger");
        return;
      }
      report(errors, okMessage);
      await reloadAll();
    } catch (e) {
      addNotification((e && (e.err || e.message)) || "Something went wrong, please contact support", "danger");
    } finally {
      setBusy(false);
    }
  };

  // DatabaseTabViewModel.ExecuteSaveItems: nothing ticked that changed
  // (GetDataChangedCount) -> "There is no items selected for saving."
  const onSave = guard("edit", () => {
    if (copyChanges <= 0) {
      addNotification("There is no items selected for saving.", "danger");
      return;
    }
    if (beforeSave) {
      const problem = beforeSave();
      if (problem) {
        addNotification(problem, "danger");
        return;
      }
    }
    run((l) => l.save(), bidlight ? "Rows copied to the company database" : "Saved");
  });


  const onSaveToVersion = guard("edit", () => {
    if (copyChanges <= 0) {
      addNotification("There is no items selected for saving data into other version.", "danger");
      return;
    }
    setVersionModal(true);
  });

  const onSaveToDefault = guard("edit", () => {
    if (copyChanges <= 0) {
      addNotification("There is no items selected for saving into default data.", "danger");
      return;
    }
    confirmDialog({
      title: "Save to BidLight default",
      content:
        "This will overwrite the existing data present in bidlight default data. Are you sure you want to continue moving the data to default?",
      onOk: () =>
        run(
          (l) => l.copyTo({ targetOrgId: workspace.userInfo.company, targetFilters: {} }),
          "Saved to the BidLight default data"
        ),
    });
  });

  return (
    <div style={{ position: "relative" }}>
      <DatabaseHeader
        title={title}
        workspace={workspace}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onRefresh={() => confirmLeave(reloadAll)}
        confirmLeave={confirmLeave}
      />
      <div style={{ paddingTop: "24px" }}>
        <DatabaseFiltrationBar
          workspace={workspace}
          pending={pending}
          onAdd={guard("edit", onAdd)}
          onEdit={guard("edit", onEdit)}
          onDelete={guard("delete", onDelete)}
          onClone={onClone ? guard("edit", onClone) : undefined}
          onSave={onSave}
          onSaveToVersion={onSaveToVersion}
          onSaveToDefault={onSaveToDefault}
          onExport={onExport}
          onImport={guard("edit", onImport)}
          onLoad={onLoad}
          onPickVersion={onPickVersion}
          onNewVersion={guard("edit", () => setNewVersionOpen(true))}
          confirmLeave={confirmLeave}
          addLabel={addLabel}
          disableAdd={disableAdd}
        />
      </div>
      {loading ? (
        <Loading color="black" text="Loading..." />
      ) : !workspace.template ? (
        <MDBContainer fluid className="pt-20">
          <p className="f-14 text-muted">
            There is no template for this company yet. Create one in the Catalogs tab first.
          </p>
        </MDBContainer>
      ) : (
        <MDBContainer fluid className="pt-20">
          {children}
        </MDBContainer>
      )}
      <NewVersionModal
        open={newVersionOpen}
        workspace={workspace}
        onCancel={() => setNewVersionOpen(false)}
        onCreate={async (panel) => {
          const problem = await workspace.createVersion(panel);
          if (!problem) {
            setNewVersionOpen(false);
            addNotification(`Version ${panel.VersionCode} created`, "success");
          }
          return problem;
        }}
      />
      <SaveToVersionModal
        open={versionModal}
        workspace={workspace}
        onCancel={() => setVersionModal(false)}
        onConfirm={(target) => {
          setVersionModal(false);
          if (target.sameAsCurrent) {
            onSave();
            return;
          }
          run(
            (l) =>
              l.copyTo({
                targetOrgId: target.targetOrgId,
                targetFilters: otherVersionFilters(target.targetTemplate, workspace.filters),
              }),
            `Saved to version ${target.targetTemplate ? target.targetTemplate.Code : ""}`
          );
        }}
      />
      {licenseType ? (
        <AcquireLicenseModal
          requestModal={!!licenseType}
          closeModal={() => setLicenseType(null)}
          requestType={licenseType}
        />
      ) : null}
    </div>
  );
};
